import React, { useCallback, useState } from 'react';
import { Typography, Tabs, Tag, Button, Popconfirm, theme } from 'antd';
import { message } from '../../../services/toastMessage';
import { IconBriefcase, IconTool, IconTruck, IconShieldCheck, IconUsersGroup, IconCheck } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  getAdminServiceProviders,
  getAdminServices,
  getAdminAccessories,
  getAdminTransportVehicles,
  getAdminInsurancePolicies,
  suspendAdminServiceProvider,
  reinstateAdminServiceProvider,
  IAdminServiceProviderRow,
  IAdminServiceRow,
  IAdminAccessoryRow,
  IAdminTransportVehicleRow,
  IAdminInsurancePolicyRow,
} from '../../../services/AdminService';
import AdminReasonModal from '../../../components/Admin/AdminReasonModal';
import CatalogListShell from './catalog/CatalogListShell';
import { CORAL } from '../../../theme/creamyTokens';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';
import { textSorter, numberSorter, dateSorter } from '../../../helpers/adminTableHelpers';

const { Title, Text } = Typography;

/**
 * Everything this marketplace rents out that is not a machine.
 *
 * Five tabs, all of which already existed end to end on the backend and none
 * of which had a single screen: the providers, their services, the
 * accessories, the transport vehicles and the insurance policies. The
 * insurance tab is the sharpest example of why this mattered -- a policy's
 * verification endpoint was reachable only by guessing an id.
 *
 * Read-only apart from suspending a provider. The platform verifies nobody's
 * trade; it decides who it carries. Insurance is the deliberate exception,
 * because a policy number is something an insurer can confirm.
 */
const CatalogSection: React.FC = () => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const [suspendUserId, setSuspendUserId] = useState<string | undefined>(undefined);
  // Bumped after an action so the shell reloads its own rows without this
  // component having to own the list itself.
  const [reloadToken, setReloadToken] = useState(0);

  const loadProviders = useCallback((state?: string, search?: string) => getAdminServiceProviders(state, search), []);
  const loadServices = useCallback((state?: string, search?: string) => getAdminServices(state, search), []);
  const loadAccessories = useCallback((state?: string, search?: string) => getAdminAccessories(state, search), []);
  const loadVehicles = useCallback((state?: string, search?: string) => getAdminTransportVehicles(state, search), []);
  const loadPolicies = useCallback((state?: string, search?: string) => getAdminInsurancePolicies(state, search), []);

  const handleSuspend = async (reason: string) => {
    if (!suspendUserId) return;
    try {
      await suspendAdminServiceProvider(suspendUserId, reason);
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      setSuspendUserId(undefined);
      setReloadToken((value) => value + 1);
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    }
  };

  const handleReinstate = async (userId: string) => {
    try {
      await reinstateAdminServiceProvider(userId);
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      setReloadToken((value) => value + 1);
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    }
  };

  const providerBadge = (row: IAdminServiceProviderRow) => {
    if (row.suspendedAt) {
      return <Tag color="red" style={{ marginInlineEnd: 0 }}>{translate(t, TRANSLATIONS.ADMIN_CATALOG_PROVIDER_SUSPENDED_BADGE)}</Tag>;
    }
    // A host's own shadow profile behaves nothing like somebody who signed
    // up to offer services, and the two are otherwise indistinguishable.
    if (row.isShadowOfHost) {
      return <Tag style={{ marginInlineEnd: 0 }}>{translate(t, TRANSLATIONS.ADMIN_CATALOG_PROVIDER_SHADOW_BADGE)}</Tag>;
    }
    return null;
  };

  const providerActions = (row: IAdminServiceProviderRow) =>
    row.suspendedAt ? (
      <Popconfirm
        title={translate(t, TRANSLATIONS.ADMIN_CATALOG_PROVIDER_REINSTATE_CONFIRM)}
        onConfirm={() => handleReinstate(row.userId)}
      >
        <Button size="small">{translate(t, TRANSLATIONS.ADMIN_OPERATORS_REINSTATE_BUTTON)}</Button>
      </Popconfirm>
    ) : (
      <Button size="small" danger onClick={() => setSuspendUserId(row.userId)}>
        {translate(t, TRANSLATIONS.ADMIN_OPERATORS_SUSPEND_BUTTON)}
      </Button>
    );

  const providersTab = (
    <CatalogListShell<IAdminServiceProviderRow>
      load={loadProviders}
      rowKey="userId"
      reloadToken={reloadToken}
      icon={<IconUsersGroup size={22} />}
      stateOptions={[
        { value: 'suspended', labelKey: TRANSLATIONS.ADMIN_CATALOG_PROVIDER_STATE_SUSPENDED },
        { value: 'independent', labelKey: TRANSLATIONS.ADMIN_CATALOG_PROVIDER_STATE_INDEPENDENT },
        { value: 'shadow', labelKey: TRANSLATIONS.ADMIN_CATALOG_PROVIDER_STATE_SHADOW },
      ]}
      toCard={(row) => ({
        key: row.userId,
        title: row.displayName || row.fullName,
        subtitle: row.email,
        badge: providerBadge(row),
        fields: [
          { label: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_SERVICES), value: `${row.activeServiceCount} / ${row.serviceCount}` },
          { label: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_DOCUMENTS), value: row.documentCount },
          { label: translate(t, TRANSLATIONS.ADMIN_OPERATORS_DETAIL_SUSPENSION_REASON), value: row.suspensionReason, wide: true },
        ],
        actions: providerActions(row),
      })}
      columns={[
        {
          title: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_PROVIDER),
          dataIndex: 'displayName',
          key: 'displayName',
          sorter: textSorter<IAdminServiceProviderRow>('displayName'),
          render: (value: string, row: IAdminServiceProviderRow) => value || row.fullName,
        },
        { title: translate(t, TRANSLATIONS.ADMIN_HOSTS_COL_NAME), dataIndex: 'email', key: 'email' },
        {
          title: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_SERVICES),
          key: 'services',
          sorter: numberSorter<IAdminServiceProviderRow>('serviceCount'),
          render: (_: unknown, row: IAdminServiceProviderRow) => `${row.activeServiceCount} / ${row.serviceCount}`,
        },
        {
          title: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_DOCUMENTS),
          dataIndex: 'documentCount',
          key: 'documentCount',
          sorter: numberSorter<IAdminServiceProviderRow>('documentCount'),
        },
        { title: '', key: 'badge', render: (_: unknown, row: IAdminServiceProviderRow) => providerBadge(row) },
        { title: '', key: 'actions', render: (_: unknown, row: IAdminServiceProviderRow) => providerActions(row) },
      ]}
    />
  );

  const servicePrice = (row: IAdminServiceRow) =>
    [row.hourlyRate != null ? `${row.hourlyRate.toFixed(2)} €/h` : null, row.fixedJobPrice != null ? `${row.fixedJobPrice.toFixed(2)} €` : null]
      .filter(Boolean)
      .join(' · ') || '—';

  const serviceBadge = (row: IAdminServiceRow) => (
    <Tag color={row.isBookable ? 'green' : 'default'} style={{ marginInlineEnd: 0 }}>
      {translate(t, row.isBookable ? TRANSLATIONS.ADMIN_CATALOG_SERVICE_BOOKABLE_BADGE : TRANSLATIONS.ADMIN_CATALOG_SERVICE_HIDDEN_BADGE)}
    </Tag>
  );

  const servicesTab = (
    <CatalogListShell<IAdminServiceRow>
      load={loadServices}
      rowKey="id"
      icon={<IconBriefcase size={22} />}
      stateOptions={[
        { value: 'bookable', labelKey: TRANSLATIONS.ADMIN_CATALOG_SERVICE_STATE_BOOKABLE },
        { value: 'hidden', labelKey: TRANSLATIONS.ADMIN_CATALOG_SERVICE_STATE_HIDDEN },
        { value: 'shadow', labelKey: TRANSLATIONS.ADMIN_CATALOG_SERVICE_STATE_SHADOW },
      ]}
      toCard={(row) => ({
        key: row.id,
        title: row.title,
        subtitle: row.providerName,
        badge: serviceBadge(row),
        fields: [
          { label: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_PRICE), value: servicePrice(row) },
          { label: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_CATEGORY), value: row.categoryName },
          {
            label: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_LOCATION),
            value: [row.city, row.countryCode].filter(Boolean).join(', '),
            wide: true,
          },
          {
            label: translate(t, TRANSLATIONS.ADMIN_CATALOG_SERVICE_SHADOW_BADGE),
            value: row.isShadowOfOperator ? <IconCheck size={16} color={token.colorSuccess} /> : undefined,
          },
        ],
      })}
      columns={[
        {
          title: translate(t, TRANSLATIONS.ADMIN_SPACES_COL_TITLE),
          dataIndex: 'title',
          key: 'title',
          sorter: textSorter<IAdminServiceRow>('title'),
        },
        {
          title: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_PROVIDER),
          dataIndex: 'providerName',
          key: 'providerName',
          sorter: textSorter<IAdminServiceRow>('providerName'),
        },
        { title: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_CATEGORY), dataIndex: 'categoryName', key: 'categoryName' },
        { title: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_PRICE), key: 'price', render: (_: unknown, row: IAdminServiceRow) => servicePrice(row) },
        {
          title: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_LOCATION),
          key: 'location',
          render: (_: unknown, row: IAdminServiceRow) => [row.city, row.countryCode].filter(Boolean).join(', '),
        },
        {
          title: translate(t, TRANSLATIONS.ADMIN_CATALOG_SERVICE_SHADOW_BADGE),
          dataIndex: 'isShadowOfOperator',
          key: 'isShadowOfOperator',
          render: (value: boolean) => (value ? <IconCheck size={16} color={token.colorSuccess} /> : '—'),
        },
        { title: '', key: 'badge', render: (_: unknown, row: IAdminServiceRow) => serviceBadge(row) },
      ]}
    />
  );

  // Attached to no machine and not sold on its own: it exists, it looks fine,
  // and nobody can ever rent it. Its own filter for exactly that reason.
  const accessoryUnrentable = (row: IAdminAccessoryRow) => row.linkedMachineryCount === 0 && !row.isIndependentlyRentable;

  const accessoriesTab = (
    <CatalogListShell<IAdminAccessoryRow>
      load={loadAccessories}
      rowKey="id"
      icon={<IconTool size={22} />}
      stateOptions={[
        { value: 'active', labelKey: TRANSLATIONS.ADMIN_CATALOG_ACCESSORY_STATE_ACTIVE },
        { value: 'inactive', labelKey: TRANSLATIONS.ADMIN_CATALOG_ACCESSORY_STATE_INACTIVE },
        { value: 'unrentable', labelKey: TRANSLATIONS.ADMIN_CATALOG_ACCESSORY_STATE_UNRENTABLE },
      ]}
      toCard={(row) => ({
        key: row.id,
        title: row.name,
        subtitle: row.hostName,
        badge: accessoryUnrentable(row) ? (
          <Tag color="orange" style={{ marginInlineEnd: 0 }}>
            {translate(t, TRANSLATIONS.ADMIN_CATALOG_ACCESSORY_UNRENTABLE_BADGE)}
          </Tag>
        ) : null,
        fields: [
          { label: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_PRICE), value: `${row.pricePerDay.toFixed(2)} €/día` },
          { label: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_QUANTITY), value: row.quantity },
          { label: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_MACHINES), value: row.linkedMachineryCount },
          {
            label: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_STANDALONE),
            value: row.isIndependentlyRentable ? <IconCheck size={16} color={token.colorSuccess} /> : '—',
          },
          {
            label: translate(t, TRANSLATIONS.ADMIN_CATALOG_ACCESSORY_UNRENTABLE_BADGE),
            value: accessoryUnrentable(row) ? translate(t, TRANSLATIONS.ADMIN_CATALOG_ACCESSORY_UNRENTABLE_HINT) : undefined,
            wide: true,
          },
        ],
      })}
      columns={[
        { title: translate(t, TRANSLATIONS.ADMIN_SPACES_COL_TITLE), dataIndex: 'name', key: 'name', sorter: textSorter<IAdminAccessoryRow>('name') },
        {
          title: translate(t, TRANSLATIONS.ADMIN_FINANCE_COL_HOST),
          dataIndex: 'hostName',
          key: 'hostName',
          sorter: textSorter<IAdminAccessoryRow>('hostName'),
        },
        {
          title: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_PRICE),
          dataIndex: 'pricePerDay',
          key: 'pricePerDay',
          sorter: numberSorter<IAdminAccessoryRow>('pricePerDay'),
          render: (value: number) => `${value.toFixed(2)} €`,
        },
        {
          title: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_QUANTITY),
          dataIndex: 'quantity',
          key: 'quantity',
          sorter: numberSorter<IAdminAccessoryRow>('quantity'),
        },
        {
          title: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_MACHINES),
          dataIndex: 'linkedMachineryCount',
          key: 'linkedMachineryCount',
          sorter: numberSorter<IAdminAccessoryRow>('linkedMachineryCount'),
        },
        {
          title: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_STANDALONE),
          dataIndex: 'isIndependentlyRentable',
          key: 'isIndependentlyRentable',
          render: (value: boolean) => (value ? <IconCheck size={16} color={token.colorSuccess} /> : '—'),
        },
        {
          title: '',
          key: 'unrentable',
          render: (_: unknown, row: IAdminAccessoryRow) =>
            accessoryUnrentable(row) ? (
              <Tag color="orange" style={{ marginInlineEnd: 0 }}>
                {translate(t, TRANSLATIONS.ADMIN_CATALOG_ACCESSORY_UNRENTABLE_BADGE)}
              </Tag>
            ) : null,
        },
      ]}
    />
  );

  const transportTab = (
    <CatalogListShell<IAdminTransportVehicleRow>
      load={loadVehicles}
      rowKey="id"
      icon={<IconTruck size={22} />}
      stateOptions={[
        { value: 'active', labelKey: TRANSLATIONS.ADMIN_CATALOG_ACCESSORY_STATE_ACTIVE },
        { value: 'inactive', labelKey: TRANSLATIONS.ADMIN_CATALOG_ACCESSORY_STATE_INACTIVE },
      ]}
      toCard={(row) => ({
        key: row.id,
        title: row.name,
        subtitle: row.hostName,
        badge: row.isActive ? null : <Tag style={{ marginInlineEnd: 0 }}>{translate(t, TRANSLATIONS.ADMIN_CATALOG_ACCESSORY_STATE_INACTIVE)}</Tag>,
        fields: [
          { label: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_PLATE), value: row.licensePlate },
          {
            label: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_CONSUMPTION),
            value: row.fuelConsumptionPer100Km != null ? `${row.fuelConsumptionPer100Km} L/100 km` : '—',
          },
        ],
      })}
      columns={[
        { title: translate(t, TRANSLATIONS.ADMIN_SPACES_COL_TITLE), dataIndex: 'name', key: 'name', sorter: textSorter<IAdminTransportVehicleRow>('name') },
        { title: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_PLATE), dataIndex: 'licensePlate', key: 'licensePlate' },
        {
          title: translate(t, TRANSLATIONS.ADMIN_FINANCE_COL_HOST),
          dataIndex: 'hostName',
          key: 'hostName',
          sorter: textSorter<IAdminTransportVehicleRow>('hostName'),
        },
        {
          title: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_CONSUMPTION),
          dataIndex: 'fuelConsumptionPer100Km',
          key: 'fuelConsumptionPer100Km',
          render: (value?: number) => (value != null ? `${value} L/100 km` : '—'),
        },
        {
          title: translate(t, TRANSLATIONS.ADMIN_BOOKINGS_COL_STATUS),
          dataIndex: 'isActive',
          key: 'isActive',
          render: (value: boolean) =>
            value ? <IconCheck size={16} color={token.colorSuccess} /> : <Tag style={{ marginInlineEnd: 0 }}>{translate(t, TRANSLATIONS.ADMIN_CATALOG_ACCESSORY_STATE_INACTIVE)}</Tag>,
        },
      ]}
    />
  );

  const policyBadge = (row: IAdminInsurancePolicyRow) => {
    // Expiry beats the verification verdict: a verified policy that ran out
    // last month is still being sold, and that is the worse fact.
    if (row.isExpired) {
      return <Tag color="red" style={{ marginInlineEnd: 0 }}>{translate(t, TRANSLATIONS.ADMIN_CATALOG_INSURANCE_EXPIRED_BADGE)}</Tag>;
    }
    return (
      <Tag
        color={row.verificationStatus === 'Verified' ? 'green' : row.verificationStatus === 'Rejected' ? 'red' : 'processing'}
        style={{ marginInlineEnd: 0 }}
      >
        {row.verificationStatus}
      </Tag>
    );
  };

  const insuranceTab = (
    <CatalogListShell<IAdminInsurancePolicyRow>
      load={loadPolicies}
      rowKey="id"
      icon={<IconShieldCheck size={22} />}
      stateOptions={[
        { value: 'pending', labelKey: TRANSLATIONS.ADMIN_CATALOG_INSURANCE_STATE_PENDING },
        { value: 'verified', labelKey: TRANSLATIONS.ADMIN_CATALOG_INSURANCE_STATE_VERIFIED },
        { value: 'rejected', labelKey: TRANSLATIONS.ADMIN_CATALOG_INSURANCE_STATE_REJECTED },
        { value: 'expired', labelKey: TRANSLATIONS.ADMIN_CATALOG_INSURANCE_STATE_EXPIRED },
      ]}
      toCard={(row) => ({
        key: row.id,
        title: row.name,
        subtitle: row.hostName,
        badge: policyBadge(row),
        fields: [
          { label: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_INSURER), value: row.insurerName ?? '—' },
          { label: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_POLICY_NUMBER), value: row.policyNumber ?? '—' },
          { label: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_PRICE), value: `${row.pricePerDay.toFixed(2)} €/día` },
          { label: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_EXCESS), value: row.excessAmount != null ? `${row.excessAmount.toFixed(2)} €` : '—' },
          {
            label: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_EXPIRES),
            value: row.expiresOn ? (
              <Text style={{ color: row.isExpired ? CORAL : undefined }}>{new Date(row.expiresOn).toLocaleDateString()}</Text>
            ) : (
              '—'
            ),
            wide: true,
          },
          { label: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_MACHINES), value: row.linkedMachineryCount },
          {
            label: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_DOCUMENT),
            value: row.hasDocument ? <IconCheck size={16} color={token.colorSuccess} /> : '—',
          },
        ],
      })}
      columns={[
        { title: translate(t, TRANSLATIONS.ADMIN_SPACES_COL_TITLE), dataIndex: 'name', key: 'name', sorter: textSorter<IAdminInsurancePolicyRow>('name') },
        {
          title: translate(t, TRANSLATIONS.ADMIN_FINANCE_COL_HOST),
          dataIndex: 'hostName',
          key: 'hostName',
          sorter: textSorter<IAdminInsurancePolicyRow>('hostName'),
        },
        { title: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_INSURER), dataIndex: 'insurerName', key: 'insurerName', render: (v?: string) => v ?? '—' },
        { title: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_POLICY_NUMBER), dataIndex: 'policyNumber', key: 'policyNumber', render: (v?: string) => v ?? '—' },
        {
          title: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_EXCESS),
          dataIndex: 'excessAmount',
          key: 'excessAmount',
          sorter: numberSorter<IAdminInsurancePolicyRow>('excessAmount'),
          render: (value?: number) => (value != null ? `${value.toFixed(2)} €` : '—'),
        },
        {
          title: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_EXPIRES),
          dataIndex: 'expiresOn',
          key: 'expiresOn',
          sorter: dateSorter<IAdminInsurancePolicyRow>('expiresOn'),
          render: (value: string | undefined, row: IAdminInsurancePolicyRow) =>
            value ? <Text style={{ color: row.isExpired ? CORAL : undefined }}>{new Date(value).toLocaleDateString()}</Text> : '—',
        },
        {
          title: translate(t, TRANSLATIONS.ADMIN_CATALOG_COL_DOCUMENT),
          dataIndex: 'hasDocument',
          key: 'hasDocument',
          render: (value: boolean) => (value ? <IconCheck size={16} color={token.colorSuccess} /> : '—'),
        },
        { title: '', key: 'badge', render: (_: unknown, row: IAdminInsurancePolicyRow) => policyBadge(row) },
      ]}
    />
  );

  return (
    <div>
      <Title level={4} style={{ marginBottom: 8 }}>
        {translate(t, TRANSLATIONS.ADMIN_NAV_CATALOG)}
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16, maxWidth: 760 }}>
        {translate(t, TRANSLATIONS.ADMIN_CATALOG_SUBTITLE)}
      </Text>

      <Tabs
        items={[
          { key: 'providers', label: translate(t, TRANSLATIONS.ADMIN_CATALOG_TAB_PROVIDERS), children: providersTab },
          { key: 'services', label: translate(t, TRANSLATIONS.ADMIN_CATALOG_TAB_SERVICES), children: servicesTab },
          { key: 'accessories', label: translate(t, TRANSLATIONS.ADMIN_CATALOG_TAB_ACCESSORIES), children: accessoriesTab },
          { key: 'transport', label: translate(t, TRANSLATIONS.ADMIN_CATALOG_TAB_TRANSPORT), children: transportTab },
          { key: 'insurance', label: translate(t, TRANSLATIONS.ADMIN_CATALOG_TAB_INSURANCE), children: insuranceTab },
        ]}
      />

      <AdminReasonModal
        open={suspendUserId !== undefined}
        title={translate(t, TRANSLATIONS.ADMIN_CATALOG_PROVIDER_SUSPEND_MODAL)}
        confirmLabel={translate(t, TRANSLATIONS.ADMIN_OPERATORS_SUSPEND_BUTTON)}
        onCancel={() => setSuspendUserId(undefined)}
        onConfirm={handleSuspend}
      />
    </div>
  );
};

export default CatalogSection;
